/* ============================================================================
   sprinkle-rain.js - ambient falling sprinkles (seasonal shapes)
   ============================================================================ */

/** heart | snowflake | leaf | flower | sparkle | dot */
/** Classic maple leaf silhouette for autumn sprinkles. viewBox 0 0 24 24. */
export const MAPLE_LEAF_PATH =
  "M12 1.6C11.6 4.2 10.8 5.6 9.1 6.8 7.2 8.1 5.2 7.9 4.5 9.6 3.8 11.3 5.1 12.4 6.6 12.7 5.4 13.8 4.4 15.6 5.2 17.3 6.1 19.2 8.1 19 9.6 18.1 9.9 20.1 10.9 21.8 12 22.6 13.1 21.8 14.1 20.1 14.4 18.1 15.9 19 17.9 19.2 18.8 17.3 19.6 15.6 18.6 13.8 17.4 12.7 18.9 12.4 20.2 11.3 19.5 9.6 18.8 7.9 16.8 8.1 14.9 6.8 13.2 5.6 12.4 4.2 12 1.6Z";

/**
 * Filled 8-ray sparkle: 4 long concave points (N/E/S/W) + 4 short diagonal needles.
 * Matches the classic sparkle mark (not stroke-only crosses or broken kite fragments).
 */
export const SPARKLE_PATH =
  "M12 1.05c.22 4.05 1.85 6.7 4.85 8.4 3 1.7 5.1 2.55 5.1 2.55s-2.1.85-5.1 2.55c-3 1.7-4.63 4.35-4.85 8.4-.22-4.05-1.85-6.7-4.85-8.4C4.15 13.85 2.05 13 2.05 13s2.1-.85 5.1-2.55c3-1.7 4.63-4.35 4.85-8.4z" +
  "M6.35 6.35l2.85 2.85-.7.7-2.85-2.85zm11.3 0l.7.7-2.85 2.85-.7-.7zm0 11.3l-2.85-2.85.7-.7 2.85 2.85zm-11.3 0l2.85-2.85.7.7-2.85 2.85z";

/** Bump when leaf/sparkle paths change so wired containers rebuild. */
export const SPRINKLE_SHAPE_VER = "maple-classic-5";

function shapeSvg(type, color) {
  if (type === "heart") {
    return `<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="${color}" d="M12 20s-7-4.5-9.5-9A4.5 4.5 0 0 1 12 6a4.5 4.5 0 0 1 9.5 5c-2.5 4.5-9.5 9-9.5 9Z"/></svg>`;
  }
  if (type === "snowflake") {
    return `<svg viewBox="0 0 24 24" aria-hidden="true"><g fill="none" stroke="${color}" stroke-width="1.6" stroke-linecap="round"><path d="M12 3v18M5 7.5l14 9M5 16.5l14-9"/><path d="M12 3l2 2M12 3l-2 2M12 21l2-2M12 21l-2-2M5 7.5l2.2.2M5 7.5l.2 2.2M19 16.5l-2.2-.2M19 16.5l-.2-2.2M5 16.5l2.2-.2M5 16.5l.2-2.2M19 7.5l-2.2.2M19 7.5l-.2 2.2"/></g></svg>`;
  }
  if (type === "leaf") {
    return `<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="${color}" d="${MAPLE_LEAF_PATH}"/></svg>`;
  }
  if (type === "flower") {
    return `<svg viewBox="0 0 24 24" aria-hidden="true"><g fill="${color}"><circle cx="12" cy="7.2" r="2.4"/><circle cx="16.5" cy="10.2" r="2.4"/><circle cx="15.2" cy="15.2" r="2.4"/><circle cx="8.8" cy="15.2" r="2.4"/><circle cx="7.5" cy="10.2" r="2.4"/><circle cx="12" cy="12" r="2.1"/></g></svg>`;
  }
  if (type === "sparkle" || type === "star") {
    return `<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="${color}" d="${SPARKLE_PATH}"/></svg>`;
  }
  return `<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="5" fill="${color}"/></svg>`;
}

/** Seasonal motif replaces hearts; sparkles + dots stay year-round (no five-point stars). */
export function seasonalSprinkleShapes(now = new Date()) {
  const m = now.getMonth() + 1;
  // Winter: Dec 1 – Feb 28/29
  if (m === 12 || m === 1 || m === 2) return ["snowflake", "sparkle", "dot", "sparkle"];
  // Autumn: Sep 1 – Nov 30
  if (m === 9 || m === 10 || m === 11) return ["leaf", "sparkle", "dot", "leaf"];
  // Summer: Jun 1 – Aug 31
  if (m === 6 || m === 7 || m === 8) return ["flower", "sparkle", "dot", "sparkle"];
  // Spring: Mar 1 – May 31 (hearts stay)
  return ["heart", "sparkle", "dot", "sparkle"];
}

function seasonKey(now = new Date()) {
  const m = now.getMonth() + 1;
  if (m === 12 || m === 1 || m === 2) return "winter";
  if (m === 9 || m === 10 || m === 11) return "autumn";
  if (m === 6 || m === 7 || m === 8) return "summer";
  return "spring";
}

/** Seasonal motifs read a bit larger than sparkles / dots. */
export function sprinklePieceSizePx(type) {
  const seasonal =
    type === "heart" || type === "snowflake" || type === "leaf" || type === "flower";
  if (seasonal) return 15 + Math.random() * 12; // 15–27
  return 9 + Math.random() * 11; // 9–20
}

function pieceCount() {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return 0;
  if (window.matchMedia("(max-width: 720px)").matches) return 21;
  if (typeof navigator !== "undefined" && navigator.connection?.saveData) return 17;
  return 38;
}

export function initSprinkleRain(container = document.getElementById("sprinkle-rain")) {
  if (!container) return;
  const season = seasonKey();
  if (
    container.dataset.wired === "1" &&
    container.dataset.season === season &&
    container.dataset.shapeVer === SPRINKLE_SHAPE_VER
  ) {
    return;
  }
  container.innerHTML = "";
  container.dataset.wired = "1";
  container.dataset.season = season;
  container.dataset.shapeVer = SPRINKLE_SHAPE_VER;

  const shapes = seasonalSprinkleShapes();
  const colors = [
    "var(--accent-soft)",
    "var(--secondary-soft)",
    "var(--accent)",
    "var(--secondary)",
    "var(--accent-deep)",
    "var(--secondary-deep)",
  ];
  const n = pieceCount();
  for (let i = 0; i < n; i++) {
    const type = shapes[i % shapes.length];
    const el = document.createElement("span");
    el.className = "sprinkle-rain__piece";
    el.innerHTML = shapeSvg(type, colors[i % colors.length]);
    const band = (i + 0.5) / Math.max(n, 1);
    const jitter = (Math.random() - 0.5) * (0.85 / Math.max(n, 1));
    el.style.left = `${Math.min(97, Math.max(2, (band + jitter) * 100))}%`;
    el.style.animationDuration = `${18 + Math.random() * 22}s`;
    el.style.animationDelay = `${Math.random() * -28}s`;
    el.style.setProperty("--drift", `${-30 + Math.random() * 60}px`);
    el.style.setProperty("--spin", `${Math.random() * 360}deg`);
    el.style.setProperty("--size", `${sprinklePieceSizePx(type)}px`);
    container.appendChild(el);
  }

  const syncFallDistance = () => {
    container.style.setProperty(
      "--fall",
      `${Math.max(container.offsetHeight, window.innerHeight) + 40}px`
    );
  };
  const syncPlayback = () => {
    container.querySelectorAll(".sprinkle-rain__piece").forEach((piece) => {
      piece.style.animationPlayState = document.hidden ? "paused" : "running";
    });
  };
  syncFallDistance();
  syncPlayback();
  window.addEventListener("resize", syncFallDistance);
  window.addEventListener("load", syncFallDistance, { once: true });
  document.addEventListener("visibilitychange", syncPlayback);
}
